/**
 * YabanciFirmaKarti.jsx — "Yabancı firma" anahtarı (yalnızca personel). Backlog #313, backend #312.
 *
 * Firma başına, tüm fuarlar için: AÇIKKEN firma Vergi Levhası (77) vermez, İSG yükü yoktur (79 isteğe
 * bağlı, 88 / 89 / 99 sunulmaz) ve çalışanları YABANCI kart yoluna girer: KVKK + Kimlik / Pasaport +
 * firmanın Firma Belgeleri'nden bir onaylı belge (#314). Vergi numarası yine zorunlu; yabancı kimlik
 * serbest metin, türü (VAT, EIN, ...) ayrıca tutulur. Not, kim ve ne zaman firma satırında (V73).
 *
 * ⚠️ AÇMAK İSG belgelerini kart kapısından hemen düşürür — yanlışlıkla yabancı işaretlenen bir Türk
 * firması İSG belgesi vermeden geçer. Bu yüzden açmak onay ister. Ülkesi Türkiye olan firma açılamaz.
 * Firma girişi bu anahtarı görmez; PUT /firma/{id}/yabanci personel dışına 403.
 *
 * @param {number}   firmaId
 * @param {Function} onChanged  `(mesaj)` — yazma başarılı olunca; sayfa belge gruplarını yeniden okur
 */
import React, { useEffect, useState } from "react";
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Col, FormGroup, Input, Label, Modal, ModalBody,
  ModalFooter, ModalHeader, Row, Spinner,
} from "reactstrap";
import { Globe } from "lucide-react";
import { getFirmaYabanci, setFirmaYabanci } from "../../services/portalService";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../i18n";

const NOT_MAX = 500;

function trTarihSaat(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" });
}

export default function YabanciFirmaKarti({ firmaId, onChanged }) {
  const { t } = useTranslation("components");
  const [durum, setDurum] = useState(null);
  const [not, setNot] = useState("");
  const [vergiNo, setVergiNo] = useState("");
  const [vergiTuru, setVergiTuru] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [uyari, setUyari] = useState(null);
  const [onayModal, setOnayModal] = useState(false);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const veri = await getFirmaYabanci(firmaId);
        if (ignore) return;
        setDurum(veri);
        setNot(veri?.not ?? "");
        setVergiNo(veri?.taxNumber ?? "");
        setVergiTuru(veri?.taxNumberLabel ?? "");
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [firmaId]);

  async function kaydet(yabanci, mesaj) {
    setSaving(true);
    setError(null);
    setUyari(null);
    try {
      const body = { yabanci, not: not.trim() };
      if ((durum?.taxNumber ?? "") !== vergiNo.trim()) body.taxNumber = vergiNo.trim();
      if (yabanci) body.taxNumberLabel = vergiTuru.trim();
      const sonuc = await setFirmaYabanci(firmaId, body);
      const yeni = sonuc?.data ?? durum;
      setDurum(yeni);
      setNot(yeni?.not ?? "");
      setVergiNo(yeni?.taxNumber ?? "");
      setVergiTuru(yeni?.taxNumberLabel ?? "");
      if (yeni?.vergiNoGecersiz) {
        setUyari(t("yabanciFirmaKarti.kayitliVergiNumarasiVknTckn"));
      } else if (yeni?.vergiNoEksik && yeni?.uyari) {
        // #435: Yabancı → Yerli with no number on file: the rule starts now. Backend #434 sends the sentence in the request's language.
        setUyari(yeni.uyari);
      }
      setOnayModal(false);
      onChanged?.(mesaj);
    } catch (e) {
      setError(e.message);
      setOnayModal(false);
    } finally {
      setSaving(false);
    }
  }

  const yabanci = !!durum?.yabanci;
  const kim = durum?.ayarlayanAdSoyad || (durum?.ayarlayanUserId != null ? `#${durum.ayarlayanUserId}` : null);
  const ne = trTarihSaat(durum?.tarih);
  const degisti = (durum?.not ?? "") !== not.trim()
    || (durum?.taxNumber ?? "") !== vergiNo.trim()
    || (yabanci && (durum?.taxNumberLabel ?? "") !== vergiTuru.trim());

  return (
    <Card className="mb-3">
      <CardHeader className="bg-transparent d-flex align-items-center gap-2">
        <Globe size={16} className="text-muted" />
        <strong className="flex-grow-1">{t("yabanciFirmaKarti.uyrukVeVergiKimligi")}</strong>
        {durum && (yabanci
          ? <Badge color="info">{t("yabanciFirmaKarti.yabanciFirma")}</Badge>
          : <Badge color="secondary">{t("yabanciFirmaKarti.yerliFirma")}</Badge>)}
      </CardHeader>
      <CardBody>
        {error && <Alert color="danger" className="py-2 small mb-3">{error}</Alert>}
        {uyari && <Alert color="warning" className="py-2 small mb-3">{uyari}</Alert>}
        {loading && !durum ? (
          <Spinner size="sm" color="primary" />
        ) : durum && (
          <>
            <FormGroup switch className="mb-2">
              <Input
                type="switch" id="yabanci-firma" checked={yabanci} disabled={saving || (!yabanci && durum.turkiye)}
                onChange={(e) => (e.target.checked
                  ? setOnayModal(true)
                  : kaydet(false, t("yabanciFirmaKarti.firmaYerliOlarakIsaretlendi")))}
              />
              <Label check for="yabanci-firma" className="fw-semibold">{t("yabanciFirmaKarti.yabanciFirma")}</Label>
              <div className="small text-muted">
                {yabanci
                  ? t("yabanciFirmaKarti.vergiLevhasiVeIsgBelgeleri")
                  : durum.turkiye
                    ? t("yabanciFirmaKarti.ulkesiTurkiyeYabanciIsaretlenemezOnce")
                    : t("yabanciFirmaKarti.yerliKurallarVergiLevhasiVe")}
              </div>
              {(kim || ne) && <div className="small text-muted">{[kim, ne].filter(Boolean).join(" · ")}</div>}
            </FormGroup>

            <Row className="g-2 align-items-end">
              <Col md={4}>
                <Label className="form-label small mb-1" for="yf-vergi">
                  {yabanci ? t("yabanciFirmaKarti.yabanciVergiKimligiIstegeBagli") : t("yabanciFirmaKarti.vergiNumarasiVknTckn")}
                </Label>
                <Input id="yf-vergi" bsSize="sm" value={vergiNo} maxLength={yabanci ? 50 : 11}
                       inputMode={yabanci ? "text" : "numeric"} disabled={saving}
                       onChange={(e) => setVergiNo(e.target.value)} />
                {yabanci && <div className="small text-muted mt-1">{t("yabanciFirmaKarti.vergiNoIstegeBagliIpucu")}</div>}
              </Col>
              {yabanci && (
                <Col md={3}>
                  <Label className="form-label small mb-1" for="yf-tur">{t("yabanciFirmaKarti.kimlikTuru")}</Label>
                  <Input id="yf-tur" bsSize="sm" value={vergiTuru} maxLength={50} placeholder="VAT, EIN, UID…"
                         disabled={saving} onChange={(e) => setVergiTuru(e.target.value)} />
                </Col>
              )}
              <Col md={yabanci ? 5 : 8}>
                <Label className="form-label small mb-1" for="yf-not">{t("yabanciFirmaKarti.not")}</Label>
                <div className="d-flex gap-2">
                  <Input id="yf-not" bsSize="sm" value={not} maxLength={NOT_MAX} placeholder={t("yabanciFirmaKarti.nedenIstegeBagli")}
                         disabled={saving} onChange={(e) => setNot(e.target.value)} />
                  <Button color="primary" outline size="sm" className="text-nowrap" disabled={saving || !degisti}
                          onClick={() => kaydet(yabanci, "Uyruk bilgisi kaydedildi.")}>
                    {saving && !onayModal ? <Spinner size="sm" /> : t("common:kaydet")}
                  </Button>
                </div>
              </Col>
            </Row>
          </>
        )}
      </CardBody>

      <Modal isOpen={onayModal} toggle={() => !saving && setOnayModal(false)}>
        <ModalHeader toggle={() => !saving && setOnayModal(false)}>{t("yabanciFirmaKarti.firmaYabanciOlarakIsaretlensinMi")}</ModalHeader>
        <ModalBody className="small">
          <p>
            {t("yabanciFirmaKarti.firmadanArtik")}{" "}<strong>{t("yabanciFirmaKarti.vergiLevhasi")}</strong>{" "}{t("yabanciFirmaKarti.ve")}{" "}<strong>{t("yabanciFirmaKarti.isgBelgeleri")}</strong>{" "}{t("yabanciFirmaKarti.isYeriHekimiIsGuvenligi")}{" "}<strong>{t("yabanciFirmaKarti.kvkkKimlikPasaport")}</strong>{" "}{t("yabanciFirmaKarti.veFirmaninBirOnayliFirma")}
          </p>
          <p className="mb-0 text-danger">
            {t("yabanciFirmaKarti.turkBirFirmayiYanlisliklaYabanci")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setOnayModal(false)} disabled={saving}>{t("yabanciFirmaKarti.vazgec")}</Button>
          <Button color="info" onClick={() => kaydet(true, t("yabanciFirmaKarti.firmaYabanciOlarakIsaretlendi"))} disabled={saving}>
            {saving ? <Spinner size="sm" /> : t("yabanciFirmaKarti.yabanciOlarakIsaretle")}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}
